import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBarcode, faPrint, faTags, faBoxes, faSlidersH, faCog,
    faTimes, faCheck, faExclamationTriangle, faSearch, faSpinner,
    faPlus, faMinus, faEye, faSync, faStore, faRefresh,
    faTrashAlt, faChevronDown, faCheckCircle, faQrcode
} from '@fortawesome/free-solid-svg-icons';
import JsBarcode from 'jsbarcode';
import './Styles/BarUI.css';

// ─────────────────────────────────────────────────────────────
// 🌍 TRANSLATION SYSTEM (keeping as is)
// ─────────────────────────────────────────────────────────────
const createBarcodeTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => cache.set(key, value));
            }
        } catch (e) { console.log("No cache found"); }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => { cacheObj[key] = value; });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) { console.error("Error saving to cache:", e); }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;
        const dict = {
            'ur': {
                'Barcode Generator': 'بارکوڈ جنریٹر', 'Select Products': 'پروڈکٹس منتخب کریں',
                'Selected': 'منتخب کردہ', 'No products selected': 'کوئی پروڈکٹ منتخب نہیں',
                'Label Content': 'لیبل مواد', 'Product Name': 'پروڈکٹ کا نام',
                'Business Name': 'کاروبار کا نام', 'Paper & Printer': 'کاغذ اور پرنٹر',
                'Label Sheet': 'لیبل شیٹ', 'Quantity': 'مقدار', 'Custom': 'اپنی مرضی',
                'Stock Qty': 'اسٹاک مقدار', 'Print Copies': 'پرنٹ کاپیاں',
                'Preview': 'پیش نظارہ', 'Print': 'پرنٹ کریں', 'Printing...': 'پرنٹ ہو رہا ہے...',
                'Cancel': 'منسوخ کریں', 'Print Now': 'ابھی پرنٹ کریں',
                'Select All': 'سب منتخب کریں', 'Clear': 'صاف کریں',
                'Search products...': 'پروڈکٹس تلاش کریں...', 'No products found': 'کوئی پروڈکٹ نہیں ملی',
                'Loading products...': 'پروڈکٹس لوڈ ہو رہے ہیں...', 'Retry': 'دوبارہ کوشش کریں',
                'Stock': 'اسٹاک', 'Rs.': 'روپے', 'Success': 'کامیابی', 'Error': 'خرابی',
                'Warning': 'انتباہ', 'setPerProduct': 'فی پروڈکٹ مقرر کریں', 'onePerUnit': '1 فی یونٹ'
            },
            'ar': {
                'Barcode Generator': 'مولد الباركود', 'Select Products': 'اختر المنتجات',
                'Selected': 'المحدد', 'No products selected': 'لم يتم اختيار منتجات',
                'Label Content': 'محتوى الملصق', 'Product Name': 'اسم المنتج',
                'Business Name': 'اسم النشاط التجاري', 'Paper & Printer': 'الورق والطابعة',
                'Label Sheet': 'ورقة الملصقات', 'Quantity': 'الكمية', 'Custom': 'مخصص',
                'Stock Qty': 'كمية المخزون', 'Print Copies': 'نسخ الطباعة',
                'Preview': 'معاينة', 'Print': 'طباعة', 'Printing...': 'جاري الطباعة...',
                'Cancel': 'إلغاء', 'Print Now': 'طباعة الآن', 'Select All': 'اختر الكل',
                'Clear': 'مسح', 'Search products...': 'بحث عن منتجات...',
                'No products found': 'لم يتم العثور على منتجات',
                'Loading products...': 'جاري تحميل المنتجات...', 'Retry': 'إعادة المحاولة',
                'Stock': 'المخزون', 'Rs.': 'روبية', 'Success': 'نجاح', 'Error': 'خطأ',
                'Warning': 'تحذير', 'setPerProduct': 'تعيين لكل منتج', 'onePerUnit': '1 لكل وحدة'
            },
            'es': {
                'Barcode Generator': 'Generador de Códigos de Barras', 'Select Products': 'Seleccionar Productos',
                'Selected': 'Seleccionados', 'No products selected': 'No hay productos seleccionados',
                'Label Content': 'Contenido de la Etiqueta', 'Product Name': 'Nombre del Producto',
                'Business Name': 'Nombre del Negocio', 'Paper & Printer': 'Papel e Impresora',
                'Label Sheet': 'Hoja de Etiquetas', 'Quantity': 'Cantidad', 'Custom': 'Personalizado',
                'Stock Qty': 'Cantidad en Stock', 'Print Copies': 'Copias de Impresión',
                'Preview': 'Vista Previa', 'Print': 'Imprimir', 'Printing...': 'Imprimiendo...',
                'Cancel': 'Cancelar', 'Print Now': 'Imprimir Ahora', 'Select All': 'Seleccionar Todo',
                'Clear': 'Limpiar', 'Search products...': 'Buscar productos...',
                'No products found': 'No se encontraron productos',
                'Loading products...': 'Cargando productos...', 'Retry': 'Reintentar',
                'Stock': 'Stock', 'Rs.': 'Rs.', 'Success': 'Éxito', 'Error': 'Error',
                'Warning': 'Advertencia', 'setPerProduct': 'Establecer por producto', 'onePerUnit': '1 por unidad'
            }
        };
        if (dict[targetLang]?.[text]) return dict[targetLang][text];
        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) { const data = await response.json(); return data.translatedText; }
        } catch (e) { console.log("Translation failed"); }
        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) return cache.get(cacheKey);
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        barcodeGenerator: 'Barcode Generator', selectProducts: 'Select Products',
        selected: 'Selected', noProductsSelected: 'No products selected',
        labelContent: 'Label Content', productName: 'Product Name',
        businessName: 'Business Name', paperPrinter: 'Paper & Printer',
        labelSheet: 'Label Sheet', quantity: 'Quantity', custom: 'Custom',
        stockQty: 'Stock Qty', printCopies: 'Print Copies', preview: 'Preview',
        print: 'Print', printing: 'Printing...', cancel: 'Cancel', printNow: 'Print Now',
        selectAll: 'Select All', clear: 'Clear', searchProducts: 'Search products...',
        noProductsFound: 'No products found', loadingProducts: 'Loading products...',
        retry: 'Retry', stock: 'Stock', rs: 'Rs.', success: 'Success', error: 'Error',
        warning: 'Warning', setPerProduct: 'Set per product', onePerUnit: '1 per unit'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        const translations = {};
        const promises = [];
        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(translateText(defaultTexts[key], lang).then(t => { translations[key] = t; }));
            }
        });
        if (promises.length > 0) { await Promise.all(promises); saveToCache(); }
        return translations;
    };

    loadFromCache();
    return { getTranslations, translateText };
};

const barcodeTranslator = createBarcodeTranslator();

// ─────────────────────────────────────────────────────────────
// 🔔 TOAST
// ─────────────────────────────────────────────────────────────
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIcon = (t) => t === 'success' ? faCheckCircle : faExclamationTriangle;
    const getTitle = (t) => ({ success: translations.success, error: translations.error, warning: translations.warning }[t] || '');
    if (!toast.show) return null;
    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon"><FontAwesomeIcon icon={getIcon(toast.type)} /></div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitle(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(p => ({ ...p, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// 🖨️ PAPER PRESETS
// ─────────────────────────────────────────────────────────────
const PAPER_PRESETS = {
    'bclp1300_50x25': {
        name: 'BCLP1300 Black Copper 50mm x 25mm',
        paper: '50mm 25mm', 
        labelsPerRow: 4,
        labelWidth: 50, 
        labelHeight: 25,
        marginTop: 0, 
        marginLeft: 0,
    }
};

const API_URL = 'https://ourzestapp.online/oz_backend/mechanics_module/mechanics_products.php';

// ─────────────────────────────────────────────────────────────
// 🏷️ BARCODE LABEL — FIXED
// ─────────────────────────────────────────────────────────────
const BarcodeLabel = ({ item, businessName }) => {
    const svgRef = useRef(null);

    useEffect(() => {
        if (!svgRef.current || !item.barcode) return;

        try {
            // Clear previous barcode
            svgRef.current.innerHTML = '';
            
            // Generate barcode
            JsBarcode(svgRef.current, item.barcode, {
                format: 'CODE128',
                width: 0.8,
                height: 30,
                displayValue: false,
                margin: 0,
                background: '#FFFFFF',
                lineColor: '#000000',
                fontSize: 0,
                font: 'monospace',
                textAlign: 'center',
                textPosition: 'bottom',
                textMargin: 0,
                fontColor: '#000000',
                flat: false,
                valid: function(valid) { return valid; }
            });

            // Force SVG to fill container
            const svg = svgRef.current;
            svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
            svg.style.width = '100%';
            svg.style.height = '100%';
            svg.style.display = 'block';
            
            // Remove any extra text elements
            const textElements = svg.querySelectorAll('text');
            textElements.forEach(el => el.style.display = 'none');

        } catch (e) {
            console.error('Barcode error:', e);
        }
    }, [item.barcode]);

    return (
        <div
            className="ozbarui-barcode-item"
            style={{
                width: '50mm',
                height: '25mm',
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'space-between',
                padding: '0.5mm',
                boxSizing: 'border-box',
                overflow: 'hidden',
                pageBreakInside: 'avoid',
                breakInside: 'avoid',
                border: 'none',
            }}
        >
            {/* SHOP NAME */}
            <div style={{
                width: '100%',
                textAlign: 'center',
                fontFamily: '"Arial Black", "Arial Bold", Arial, Helvetica, sans-serif',
                fontSize: '6pt',
                fontWeight: '900',
                color: '#000000',
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                padding: '0.3mm 0.5mm',
                lineHeight: '1',
                flexShrink: 0,
            }}>
                {businessName}
            </div>

            {/* BARCODE */}
            <div style={{
                flex: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.2mm 0.5mm',
                minHeight: 0,
            }}>
                <svg
                    ref={svgRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'block',
                        maxHeight: '100%',
                    }}
                />
            </div>

            {/* PRODUCT NAME */}
            <div style={{
                width: '100%',
                textAlign: 'center',
                fontFamily: '"Arial Black", "Arial Bold", Arial, Helvetica, sans-serif',
                fontSize: '5.5pt',
                fontWeight: '900',
                color: '#000000',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                padding: '0.2mm 0.5mm 0.3mm',
                lineHeight: '1',
                flexShrink: 0,
            }}>
                {item.productName}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// 🚀 MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const MechanicsBarcode = () => {
    const { user, currentShop } = useAuth();

    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [currentThemeColor, setCurrentThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#10b981');

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);

    const businessName = user?.shop_name || user?.business_name || 'Your Business';

    const [selectedPaper] = useState('bclp1300_50x25');
    const [printMode, setPrintMode] = useState('custom');
    const [printCopies, setPrintCopies] = useState(1);

    const [previewItems, setPreviewItems] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });

    const printRef = useRef(null);
    const abortControllerRef = useRef(null);
    const toastTimerRef = useRef(null);
    const printTimeoutRef = useRef(null);

    // ── THEME ──
    const applyThemeToDocument = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';
        document.documentElement.style.setProperty('--ozbarui-primary', color);
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        let style = document.getElementById('ozbarui-theme-styles');
        if (!style) { style = document.createElement('style'); style.id = 'ozbarui-theme-styles'; document.head.appendChild(style); }
        style.textContent = `
            .ozbarui-header-icon { background: linear-gradient(135deg, ${color}, rgba(${r},${g},${b},0.8)) !important; }
            .ozbarui-btn-print { background: linear-gradient(135deg, ${color}, rgba(${r},${g},${b},0.8)) !important; }
            .ozbarui-btn-primary { background: ${color} !important; }
            .ozbarui-btn-small:hover:not(:disabled) { background: ${color} !important; border-color: ${color} !important; }
            .ozbarui-product-item.ozbarui-selected { background: rgba(${r},${g},${b},0.05) !important; border-color: ${color} !important; }
            .ozbarui-mode-option.ozbarui-active { border-color: ${color} !important; background: rgba(${r},${g},${b},0.05) !important; }
            .ozbarui-search-box input:focus { border-color: ${color} !important; box-shadow: 0 0 0 3px rgba(${r},${g},${b},0.1) !important; }
            .ozbarui-panel-header h3 svg { color: ${color} !important; }
            .ozbarui-price { color: ${color} !important; }
            .ozbarui-loading-spinner { border-top-color: ${color} !important; }
            .ozbarui-quantity-control button:hover:not(:disabled) { background: ${color} !important; border-color: ${color} !important; }
            .ozbarui-header-bg-animation .gradient-orb.orb-1 { background: radial-gradient(circle, rgba(${r},${g},${b},0.4) 0%, rgba(${r},${g},${b},0.1) 100%); }
            .ozbarui-header-bg-animation .gradient-orb.orb-2 { background: radial-gradient(circle, rgba(${r},${g},${b},0.35) 0%, rgba(${r},${g},${b},0.08) 100%); }
            .ozbarui-header-bg-animation .gradient-orb.orb-3 { background: radial-gradient(circle, rgba(${r},${g},${b},0.3) 0%, rgba(${r},${g},${b},0.05) 100%); }
            .ozbarui-header-bg-animation .gradient-orb.orb-4 { background: radial-gradient(circle, rgba(${r},${g},${b},0.25) 0%, rgba(${r},${g},${b},0.03) 100%); }
            .ozbarui-header-bg-animation .gradient-orb.orb-5 { background: radial-gradient(circle, rgba(${r},${g},${b},0.2) 0%, rgba(${r},${g},${b},0.02) 100%); }
        `;
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        applyThemeToDocument(localStorage.getItem('ourzest_theme') || '#10b981');
        const onStorage = (e) => { if (e.key === 'ourzest_theme') applyThemeToDocument(e.newValue || '#10b981'); };
        const onTheme = (e) => { if (e.detail?.theme) applyThemeToDocument(e.detail.theme); };
        window.addEventListener('storage', onStorage);
        window.addEventListener('themeChanged', onTheme);
        const interval = setInterval(() => {
            const t = localStorage.getItem('ourzest_theme') || '#10b981';
            if (t !== currentThemeColor) applyThemeToDocument(t);
        }, 1000);
        return () => { window.removeEventListener('storage', onStorage); window.removeEventListener('themeChanged', onTheme); clearInterval(interval); };
    }, [applyThemeToDocument, currentThemeColor]);

    // ── DARK MODE ──
    useEffect(() => {
        const sync = () => {
            const isDark = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDark);
            const c = document.querySelector('.ozbarui-container');
            if (c) isDark ? c.classList.add('ozbarui-dark') : c.classList.remove('ozbarui-dark');
            isDark ? document.body.classList.add('ozbarui-dark') : document.body.classList.remove('ozbarui-dark');
        };
        sync();
        const onStorage = (e) => { if (e.key === 'ourzest_darkMode') sync(); };
        const onToggle = (e) => {
            const isDark = e.detail?.darkMode ?? localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDark);
            const c = document.querySelector('.ozbarui-container');
            if (c) isDark ? c.classList.add('ozbarui-dark') : c.classList.remove('ozbarui-dark');
        };
        window.addEventListener('storage', onStorage);
        window.addEventListener('darkModeToggled', onToggle);
        const interval = setInterval(() => { if ((localStorage.getItem('ourzest_darkMode') === 'true') !== darkMode) sync(); }, 1000);
        return () => { window.removeEventListener('storage', onStorage); window.removeEventListener('darkModeToggled', onToggle); clearInterval(interval); };
    }, [darkMode]);

    // ── LANGUAGE ──
    useEffect(() => {
        const load = async (lang) => setTranslations(await barcodeTranslator.getTranslations(lang));
        const onStorage = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) { setCurrentLanguage(lang); load(lang); }
        };
        const onCustom = (e) => {
            if (e.detail?.language && e.detail.language !== currentLanguage) { setCurrentLanguage(e.detail.language); load(e.detail.language); }
        };
        load(currentLanguage);
        window.addEventListener('storage', onStorage);
        window.addEventListener('languageChanged', onCustom);
        const interval = setInterval(() => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) { setCurrentLanguage(lang); load(lang); }
        }, 1000);
        return () => { window.removeEventListener('storage', onStorage); window.removeEventListener('languageChanged', onCustom); clearInterval(interval); };
    }, [currentLanguage]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    useEffect(() => {
        const c = document.querySelector('.ozbarui-container');
        if (c) { isRTL ? c.classList.add('ozbarui-rtl') : c.classList.remove('ozbarui-rtl'); isRTL ? c.classList.remove('ozbarui-ltr') : c.classList.add('ozbarui-ltr'); }
        document.documentElement.dir = document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, [isRTL]);

    // ── TOAST ──
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => setToast(p => ({ ...p, show: false })), 3000);
    }, []);
    useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

    // ── FETCH PRODUCTS ──
    const fetchProducts = useCallback(async () => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();
        if (!user?.username) { setLoading(false); return; }
        const shopId = currentShop?.shop_id;
        if (!shopId) { setLoading(false); setFetchError('Select a shop first'); showToast('warning', 'Select a shop first'); return; }
        setLoading(true); setFetchError(null);
        try {
            const res = await fetch(API_URL, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_records', username: user.username.trim(), shop_id: shopId, sort: 'product_name', order: 'ASC' }),
                signal: abortControllerRef.current.signal
            });
            const data = await res.json();
            if (data.status === 'success') {
                const list = Array.isArray(data.data) ? data.data : [];
                setProducts(list); setFilteredProducts(list);
            } else { setFetchError(data.message || 'Failed to fetch products'); setProducts([]); setFilteredProducts([]); }
        } catch (e) { if (e.name !== 'AbortError') setFetchError(e.message || 'Network error'); }
        finally { setLoading(false); }
    }, [user, currentShop, showToast]);

    useEffect(() => { if (user?.username && currentShop) fetchProducts(); return () => abortControllerRef.current?.abort(); }, [user, currentShop, fetchProducts]);

    // ── SEARCH ──
    useEffect(() => {
        if (!searchTerm.trim()) { setFilteredProducts(products); return; }
        const t = searchTerm.toLowerCase();
        setFilteredProducts(products.filter(p => p.product_name?.toLowerCase().includes(t) || p.batch_no?.toLowerCase().includes(t) || p.barcode?.toLowerCase().includes(t)));
    }, [searchTerm, products]);

    // ── SELECTION ──
    const toggleProductSelection = useCallback((product) => {
        setSelectedProducts(prev => prev.find(p => p.id === product.id)
            ? prev.filter(p => p.id !== product.id)
            : [...prev, { ...product, quantity: 1, originalQuantity: parseFloat(product.quantity) || 0 }]);
    }, []);
    const selectAllProducts = useCallback(() => {
        if (!filteredProducts.length) return;
        setSelectedProducts(filteredProducts.map(p => ({ ...p, quantity: 1, originalQuantity: parseFloat(p.quantity) || 0 })));
        showToast('success', `Selected ${filteredProducts.length} products`);
    }, [filteredProducts, showToast]);
    const clearSelection = useCallback(() => { setSelectedProducts([]); showToast('info', 'Selection cleared'); }, [showToast]);
    const updateSelectedQuantity = useCallback((id, qty) => setSelectedProducts(prev => prev.map(p => p.id === id ? { ...p, quantity: Math.max(1, qty || 1) } : p)), []);
    const removeSelectedProduct = useCallback((id) => setSelectedProducts(prev => prev.filter(p => p.id !== id)), []);

    // ── PREVIEW ──
    const generatePreview = useCallback(() => {
        if (!selectedProducts.length) { 
            showToast('error', 'Please select at least one product'); 
            return; 
        }
        
        const items = [];
        selectedProducts.forEach(product => {
            // Calculate quantity per product
            let qtyPerProduct = 1;
            if (printMode === 'stock') {
                qtyPerProduct = parseInt(product.originalQuantity) || 0;
            } else {
                qtyPerProduct = product.quantity || 1;
            }
            
            // Skip if quantity is 0 or negative
            if (qtyPerProduct <= 0) return;
            
            // Generate labels for this product (qtyPerProduct * printCopies)
            const totalLabels = qtyPerProduct * printCopies;
            for (let i = 0; i < totalLabels; i++) {
                items.push({ 
                    id: `${product.id}_${i}_${Date.now()}_${Math.random()}`, 
                    product: product,
                    barcode: product.barcode, 
                    productName: product.product_name 
                });
            }
        });
        
        if (!items.length) { 
            showToast('error', 'No valid items to print'); 
            return; 
        }
        
        setPreviewItems(items); 
        setShowPreview(true); 
        showToast('success', `${items.length} labels ready`);
    }, [selectedProducts, printMode, printCopies, showToast]);

    const getPaperSettings = useCallback(() => PAPER_PRESETS[selectedPaper] || PAPER_PRESETS.bclp1300_50x25, [selectedPaper]);

    // ── FIXED PRINT FUNCTION ──
    const handlePrintNow = useCallback(() => {
        if (!previewItems.length) {
            showToast('error', 'No labels to print. Generate preview first.');
            return;
        }

        // Clear any existing timeout
        if (printTimeoutRef.current) {
            clearTimeout(printTimeoutRef.current);
            printTimeoutRef.current = null;
        }

        setIsPrinting(true);

        // Use a small delay to ensure the DOM is ready
        printTimeoutRef.current = setTimeout(() => {
            try {
                const printWindow = window.open('', '_blank', 'width=800,height=600');
                if (!printWindow) {
                    showToast('error', 'Please allow popups for printing');
                    setIsPrinting(false);
                    return;
                }

                // Get the print content
                const printContent = printRef.current;
                if (!printContent) {
                    showToast('error', 'Print content not found');
                    setIsPrinting(false);
                    return;
                }

                // Clone the content to avoid affecting the UI
                const clone = printContent.cloneNode(true);
                
                // Build the print document
                printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>Print Barcodes</title>
                        <style>
                            @page {
                                size: 50mm 25mm landscape;
                                margin: 0;
                                padding: 0;
                            }
                            
                            * {
                                margin: 0;
                                padding: 0;
                                box-sizing: border-box;
                            }
                            
                            body {
                                margin: 0;
                                padding: 0;
                                background: white;
                            }
                            
                            .print-grid {
                                display: grid;
                                grid-template-columns: repeat(auto-fill, 50mm);
                                gap: 0;
                                padding: 0;
                                width: 100%;
                            }
                            
                            .barcode-item {
                                width: 50mm;
                                height: 25mm;
                                background: white;
                                display: flex;
                                flex-direction: column;
                                align-items: stretch;
                                justify-content: space-between;
                                padding: 0.5mm;
                                box-sizing: border-box;
                                overflow: hidden;
                                page-break-inside: avoid;
                                break-inside: avoid;
                                border: none;
                            }
                            
                            .shop-name {
                                width: 100%;
                                text-align: center;
                                font-family: "Arial Black", "Arial Bold", Arial, Helvetica, sans-serif;
                                font-size: 6pt;
                                font-weight: 900;
                                color: #000000;
                                text-transform: uppercase;
                                letter-spacing: 1.5px;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                padding: 0.3mm 0.5mm;
                                line-height: 1;
                                flex-shrink: 0;
                            }
                            
                            .barcode-container {
                                flex: 1;
                                width: 100%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                padding: 0.2mm 0.5mm;
                                min-height: 0;
                            }
                            
                            .barcode-container svg {
                                width: 100%;
                                height: 100%;
                                display: block;
                            }
                            
                            .product-name {
                                width: 100%;
                                text-align: center;
                                font-family: "Arial Black", "Arial Bold", Arial, Helvetica, sans-serif;
                                font-size: 5.5pt;
                                font-weight: 900;
                                color: #000000;
                                text-transform: uppercase;
                                letter-spacing: 0.5px;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                padding: 0.2mm 0.5mm 0.3mm;
                                line-height: 1;
                                flex-shrink: 0;
                            }
                            
                            @media print {
                                body { margin: 0; padding: 0; }
                                .print-grid { display: grid; }
                            }
                        </style>
                    </head>
                    <body>
                        <div class="print-grid">
                            ${Array.from(clone.querySelectorAll('.ozbarui-barcode-item')).map(el => {
                                // Extract data from the cloned element
                                const shopName = el.querySelector('div:first-child')?.textContent || businessName;
                                const productName = el.querySelector('div:last-child')?.textContent || '';
                                const svgContent = el.querySelector('svg')?.outerHTML || '';
                                
                                return `
                                    <div class="barcode-item">
                                        <div class="shop-name">${shopName}</div>
                                        <div class="barcode-container">${svgContent}</div>
                                        <div class="product-name">${productName}</div>
                                    </div>
                                `;
                            }).join('')}
                        </div>
                        <script>
                            window.onload = function() {
                                window.print();
                                window.close();
                            };
                        </script>
                    </body>
                    </html>
                `);
                
                printWindow.document.close();
                
                // Reset printing state after print dialog closes
                printTimeoutRef.current = setTimeout(() => {
                    setIsPrinting(false);
                    setShowPreview(false);
                    showToast('success', `${previewItems.length} labels printed successfully`);
                    printTimeoutRef.current = null;
                }, 2000);
                
            } catch (error) {
                console.error('Print error:', error);
                showToast('error', `Print failed: ${error.message}`);
                setIsPrinting(false);
                printTimeoutRef.current = null;
            }
        }, 300);
    }, [previewItems, businessName, showToast]);

    // ─────────────────────────────────────────────────────────
    // 🎨 RENDER
    // ─────────────────────────────────────────────────────────
    return (
        <div className={`ozbarui-container ${isRTL ? 'ozbarui-rtl' : 'ozbarui-ltr'} ${darkMode ? 'ozbarui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            {/* Sticky Header */}
            <div className="ozbarui-sticky-header">
                <div className="ozbarui-header-bg-animation">
                    <div className="gradient-orb orb-1" /><div className="gradient-orb orb-2" />
                    <div className="gradient-orb orb-3" /><div className="gradient-orb orb-4" />
                    <div className="gradient-orb orb-5" />
                </div>
                <div className="ozbarui-header-content">
                    <div className="ozbarui-header-left">
                        <div className="ozbarui-header-icon"><FontAwesomeIcon icon={faQrcode} /></div>
                        <div className="ozbarui-header-title">
                            <h1>{translations.barcodeGenerator || 'Barcode Generator'}</h1>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="ozbarui-main-content">

                {/* Left Panel */}
                <div className="ozbarui-left-panel">
                    <div className="ozbarui-panel-header">
                        <h3><FontAwesomeIcon icon={faBoxes} /> {translations.selectProducts || 'Select Products'}</h3>
                        <div className="ozbarui-panel-actions">
                            <button onClick={selectAllProducts} className="ozbarui-btn-small" disabled={!filteredProducts.length || loading}>
                                {translations.selectAll || 'Select All'}
                            </button>
                            <button onClick={clearSelection} className="ozbarui-btn-small" disabled={!selectedProducts.length}>
                                {translations.clear || 'Clear'}
                            </button>
                        </div>
                    </div>
                    <div className="ozbarui-search-box">
                        <FontAwesomeIcon icon={faSearch} className="ozbarui-search-icon" />
                        <input type="text" placeholder={translations.searchProducts || 'Search products...'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    <div className="ozbarui-products-list">
                        {loading ? (
                            <div className="ozbarui-loading-container">
                                <div className="ozbarui-loading-spinner" />
                                <p>{translations.loadingProducts || 'Loading products...'}</p>
                            </div>
                        ) : fetchError ? (
                            <div className="ozbarui-error-container">
                                <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
                                <p>{fetchError}</p>
                                <button onClick={fetchProducts} className="ozbarui-btn-retry">
                                    <FontAwesomeIcon icon={faRefresh} /> {translations.retry || 'Retry'}
                                </button>
                            </div>
                        ) : !filteredProducts.length ? (
                            <div className="ozbarui-no-products">
                                <FontAwesomeIcon icon={faBoxes} size="2x" />
                                <p>{translations.noProductsFound || 'No products found'}</p>
                            </div>
                        ) : filteredProducts.map(product => {
                            const isSelected = selectedProducts.some(p => p.id === product.id);
                            return (
                                <div key={product.id} className={`ozbarui-product-item ${isSelected ? 'ozbarui-selected' : ''}`} onClick={() => toggleProductSelection(product)}>
                                    <div className="ozbarui-product-checkbox">
                                        <input type="checkbox" checked={isSelected} onChange={() => toggleProductSelection(product)} onClick={(e) => e.stopPropagation()} />
                                    </div>
                                    <div className="ozbarui-product-info">
                                        <div className="ozbarui-product-name-col"><div className="ozbarui-product-name" title={product.product_name}>{product.product_name}</div></div>
                                        <div className="ozbarui-product-batch-col"><div className="ozbarui-product-batch" title={product.batch_no || 'N/A'}>{product.batch_no || '-'}</div></div>
                                        <div className="ozbarui-product-code-col"><div className="ozbarui-product-code" title={product.product_code || 'N/A'}>{product.product_code || '-'}</div></div>
                                        <div className="ozbarui-product-stock-col"><div className="ozbarui-stock">{parseFloat(product.quantity).toFixed(2)}</div></div>
                                        <div className="ozbarui-product-price-col"><div className="ozbarui-price">{translations.rs || 'Rs.'} {parseFloat(product.sell_price).toFixed(2)}</div></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Right Panel */}
                <div className="ozbarui-right-panel">
                    <div className="ozbarui-selected-section">
                        <h3><FontAwesomeIcon icon={faTags} /> {translations.selected || 'Selected'} ({selectedProducts.length})</h3>
                        {!selectedProducts.length ? (
                            <div className="ozbarui-no-selection"><p>{translations.noProductsSelected || 'No products selected'}</p></div>
                        ) : (
                            <div className="ozbarui-selected-items-list">
                                {selectedProducts.map(product => (
                                    <div key={product.id} className="ozbarui-selected-item">
                                        <div className="ozbarui-item-info">
                                            <div className="ozbarui-item-name-col"><div className="ozbarui-item-name" title={product.product_name}>{product.product_name}</div></div>
                                            <div className="ozbarui-item-batch-col"><div className="ozbarui-item-batch" title={product.batch_no || 'N/A'}>{product.batch_no || '-'}</div></div>
                                        </div>
                                        <div className="ozbarui-item-controls">
                                            <div className="ozbarui-quantity-control">
                                                <button onClick={() => updateSelectedQuantity(product.id, product.quantity - 1)} disabled={product.quantity <= 1 || printMode === 'stock'}><FontAwesomeIcon icon={faMinus} size="xs" /></button>
                                                <input type="number" min="1" value={product.quantity} onChange={(e) => updateSelectedQuantity(product.id, parseInt(e.target.value) || 1)} disabled={printMode === 'stock'} />
                                                <button onClick={() => updateSelectedQuantity(product.id, product.quantity + 1)} disabled={printMode === 'stock'}><FontAwesomeIcon icon={faPlus} size="xs" /></button>
                                            </div>
                                            <button className="ozbarui-remove-btn" onClick={() => removeSelectedProduct(product.id)}><FontAwesomeIcon icon={faTimes} /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="ozbarui-settings-section">
                        <h3><FontAwesomeIcon icon={faSlidersH} /> {translations.quantity || 'Quantity'}</h3>
                        <div className="ozbarui-print-mode-selector">
                            <label className={`ozbarui-mode-option ${printMode === 'custom' ? 'ozbarui-active' : ''}`}>
                                <input type="radio" name="mode" value="custom" checked={printMode === 'custom'} onChange={(e) => setPrintMode(e.target.value)} />
                                <div className="ozbarui-mode-content">
                                    <span className="ozbarui-mode-title">{translations.custom || 'Custom'}</span>
                                    <small>{translations.setPerProduct || 'Set per product'}</small>
                                </div>
                            </label>
                            <label className={`ozbarui-mode-option ${printMode === 'stock' ? 'ozbarui-active' : ''}`}>
                                <input type="radio" name="mode" value="stock" checked={printMode === 'stock'} onChange={(e) => setPrintMode(e.target.value)} />
                                <div className="ozbarui-mode-content">
                                    <span className="ozbarui-mode-title">{translations.stockQty || 'Stock Qty'}</span>
                                    <small>{translations.onePerUnit || '1 per unit'}</small>
                                </div>
                            </label>
                        </div>
                        {/* <div className="ozbarui-print-copies">
                            <label>{translations.printCopies || 'Print Copies'}: {printCopies}</label>
                            <input type="number" min="1" max="100" value={printCopies} onChange={(e) => setPrintCopies(parseInt(e.target.value) || 1)} />
                        </div> */}
                    </div>

                    <div className="ozbarui-settings-section" style={{ opacity: 0.5 }}>
                        <h3><FontAwesomeIcon icon={faPrint} /> {translations.paperPrinter || 'Paper & Printer'}</h3>
                        <div className="ozbarui-settings-grid">
                            <div className="ozbarui-setting-item ozbarui-full-width">
                                <label>{translations.labelSheet || 'Label Sheet'}</label>
                                <select value={selectedPaper} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }}>
                                    <option value="bclp1300_50x25">BCLP1300 Black Copper 50mm x 25mm</option>
                                </select>
                                <small style={{ display: 'block', marginTop: '2px', fontSize: '8pt', color: '#10b981' }}>
                                    ✅ Default: BCLP1300 Black Copper 50mm x 25mm
                                </small>
                            </div>
                        </div>
                    </div>

                    <div className="ozbarui-action-buttons">
                        <button className="ozbarui-btn-preview" onClick={generatePreview} disabled={!selectedProducts.length}>
                            <FontAwesomeIcon icon={faEye} /> {translations.preview || 'Preview'}
                        </button>
                        {/* <button className="ozbarui-btn-print" onClick={handlePrintNow} disabled={!previewItems.length || isPrinting}>
                            {isPrinting
                                ? <><FontAwesomeIcon icon={faSpinner} spin /> {translations.printing || 'Printing...'}</>
                                : <><FontAwesomeIcon icon={faPrint} /> {translations.print || 'Print'} ({previewItems.length})</>}
                        </button> */}
                    </div>
                </div>
            </div>

            {/* Preview Modal */}
            {showPreview && (
                <div className="ozbarui-preview-modal" onClick={() => {
                    if (!isPrinting) setShowPreview(false);
                }}>
                    <div className="ozbarui-preview-content" onClick={(e) => e.stopPropagation()}>
                        <div className="ozbarui-preview-header">
                            <h3><FontAwesomeIcon icon={faEye} /> {translations.preview || 'Print Preview'}</h3>
                            <button className="ozbarui-close-btn" onClick={() => {
                                if (!isPrinting) setShowPreview(false);
                            }} disabled={isPrinting}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="ozbarui-preview-body">
                            <div className="ozbarui-preview-info">
                                <span>📄 {previewItems.length} labels</span>
                                <span>📐 {getPaperSettings().name}</span>
                                <span>🔄 {printCopies} copy(ies) per product</span>
                            </div>
                            {/* ── PRINT TARGET ── */}
                            <div
                                ref={printRef}
                                className="ozbarui-print-grid"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, 50mm)',
                                    gap: '0',
                                    padding: '0',
                                    background: '#fff',
                                    width: '100%',
                                    justifyContent: 'flex-start',
                                }}
                            >
                                {previewItems.map((item, idx) => (
                                    <BarcodeLabel key={item.id || idx} item={item} businessName={businessName} />
                                ))}
                            </div>
                        </div>
                        <div className="ozbarui-preview-footer">
                            <button className="ozbarui-btn-secondary" onClick={() => {
                                if (!isPrinting) setShowPreview(false);
                            }} disabled={isPrinting}>
                                {translations.cancel || 'Cancel'}
                            </button>
                            <button className="ozbarui-btn-primary" onClick={handlePrintNow} disabled={isPrinting}>
                                {isPrinting ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> Printing...</>
                                ) : (
                                    <><FontAwesomeIcon icon={faPrint} /> {translations.printNow || 'Print Now'}</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MechanicsBarcode;